import { useEffect, useLayoutEffect, useRef } from "react";
import { bool, string, object, node, arrayOf, func, oneOfType, number } from "prop-types";
import Sortable from "sortablejs";
import { clone } from "lodash";
import useStyle from "./SortableList.style";
import classnames from "classnames";

export const SortableList = ({
  list: inheritedList = [],
  group,
  onListAdd,
  onListReorderStart,
  onListReorder,
  disabled,
  useHandle,
  className,
  displayAsGrid,
  displayAsFlexbox,
  displayAsColumnFlexbox,
  children,
  style,
  path,
  itemIdKey="id",
  forceFallback=true,
  fallbackOnBody,
}) => {
  const classes = useStyle();
  
  const gridRef = useRef(null);
  const sortableJsRef = useRef(null);
  const list = useRef(inheritedList?.map((item) => ({ id: item[itemIdKey] })));
  
  list.current = inheritedList?.map((item) => ({ id: item[itemIdKey] }));
  
  useEffect(() => {
    const d = Boolean(disabled);
    if(d !== sortableJsRef.current.option("disabled")) {
      sortableJsRef.current.option("disabled", d);
    }
  }, [disabled]);
  
  useEffect(() => {
    sortableJsRef.current.option("onStart", handleListReorderStart);
  }, [ onListReorderStart ]);
  
  useEffect(() => {
    sortableJsRef.current.option("onEnd", handleListReorder);
  }, [ onListReorder ]);
  
  useEffect(() => {
    sortableJsRef.current.option("onAdd", handleAdd);
  }, [ onListAdd ]);
  
  useLayoutEffect(() => {
    sortableJsRef.current = new Sortable(gridRef.current, {
      group,
      animation: 200,
      fallbackTolerance: 10,
      preventOnFilter: false,
      scroll: true,
      bubbleScroll: true,
      forceFallback,
      fallbackOnBody,
      ghostClass: "sortable-placeholder",
      filter: ".noDrag",
      handle: useHandle ? ".dragHandle" : undefined,
      onAdd: handleAdd,
      onEnd: handleListReorder,
      onStart: handleListReorderStart,
    });
  }, [ ]);
  
  const handleListReorderStart = () => {
    onListReorderStart?.();
  }
  
  const handleAdd = (e) => {
    onListAdd?.({
      newIndex: e.newIndex,
      oldIndex: e.oldIndex,
      from: e.from.id,
      to: e.to.id,
      item: e.item.dataset.id,
    });
  }

  const handleListReorder = (e) => {
    const l = clone(list.current);
    const movedElement = l.splice(e.oldIndex, 1)[ 0 ];
    const isRemoved = e.from.id !== e.to.id;
    
    if ( !movedElement ) {
      console.log(e, l)
      return;
    }
    
    if ( isRemoved ) {
      
      const nextSibling = Sortable.utils.getChild(e.from, e.oldIndex, {});
      if ( nextSibling ) {
        e.from.insertBefore(e.item, nextSibling);
      } else {
        e.from.appendChild(e.item);
      }
    } else {
      l.splice(e.newIndex, 0, movedElement);
    }
    
    onListReorder && onListReorder(clone(l), movedElement.id, isRemoved, e.oldIndex, e.newIndex);
    
    return !isRemoved;
  }

  return (
    <div
      id={path}
      ref={gridRef}
      className={classnames(
        "sortable",
        displayAsGrid && classes.grid,
        displayAsFlexbox && classes.flexbox,
        displayAsFlexbox && "flexbox",
        displayAsColumnFlexbox && classes.columnFlexbox,
        displayAsColumnFlexbox && "columnFlexbox",
        className
      )}
      style={style}
    >
      {children}
    </div>
  )
};

SortableList.propTypes = {
  list: arrayOf(object),
  children: oneOfType([string, object, node]),
  group: string,
  onListAdd: func,
  onListReorderStart: func,
  onListReorder: func,
  disabled: bool,
  useHandle: bool,
  className: string,
  style: object,
  path: oneOfType([string, number]),
  itemIdKey: string,
  displayAsGrid: bool,
  displayAsFlexbox: bool,
  displayAsColumnFlexbox: bool,
  forceFallback: bool,
  fallbackOnBody: bool,
};
